import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import {
  Undo as UndoIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

const API_BASE_URL = 'https://ccc4cs.choicegroup.in/api';

// Calculate total pounds from pack_size, cartons, and loose_bags
const calculateTotalPounds = (packSize, cartons, looseBags = 0) => {
  if (!packSize || (!cartons && !looseBags)) return 0;
  
  // Replace X with / for consistency
  const normalizedPackSize = packSize.replace(/X/gi, '/');
  
  // Parse pack_size format: "X/Y UNIT" (e.g., "3/7 LB", "12/16 OZ", "1/10 KG")
  const match = normalizedPackSize.match(/^(\d+)\/(\d+)\s*(LB|OZ|KG)$/i);
  
  if (!match) {
    // Handle special cases like "LOOSE KG"
    if (normalizedPackSize.toUpperCase() === 'LOOSE KG') {
      return 0; // Can't calculate without weight info
    }
    return 0;
  }
  
  const bagsPerCarton = parseInt(match[1], 10);
  const weightPerBag = parseInt(match[2], 10);
  const unit = match[3].toUpperCase();
  
  let totalPounds = 0;
  
  if (unit === 'LB') {
    // Formula: bags_per_carton * weight_per_bag * cartons + loose_bags * weight_per_bag
    totalPounds = (bagsPerCarton * weightPerBag * cartons) + (looseBags * weightPerBag);
  } else if (unit === 'OZ') {
    // Formula: (bags_per_carton * weight_per_bag * cartons) / 16 + (loose_bags * weight_per_bag) / 16
    totalPounds = ((bagsPerCarton * weightPerBag * cartons) / 16) + ((looseBags * weightPerBag) / 16);
  } else if (unit === 'KG') {
    // Formula: bags_per_carton * weight_per_bag * cartons * 2.2 + loose_bags * weight_per_bag * 2.2
    totalPounds = (bagsPerCarton * weightPerBag * cartons * 2.2) + (looseBags * weightPerBag * 2.2);
  }
  
  return Math.round(totalPounds * 100) / 100; // Round to 2 decimal places
};

const ReturnToInventoryModal = ({ open, onClose, shipment, onSuccess }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [returnReasons, setReturnReasons] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleClose = () => {
    setSelectedItems([]);
    setReturnReasons({});
    setError('');
    onClose();
  };

  const handleItemSelect = (itemId) => {
    setSelectedItems(prev => {
      if (prev.includes(itemId)) {
        return prev.filter(id => id !== itemId);
      } else {
        return [...prev, itemId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedItems.length === shipment.items.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(shipment.items.map(item => item.id));
    }
  };

  const handleReasonChange = (itemId, reason) => {
    setReturnReasons(prev => ({
      ...prev,
      [itemId]: reason
    }));
  };

  const handleSubmit = async () => {
    if (selectedItems.length === 0) {
      setError('Please select at least one item to return');
      return;
    }

    // Validate that all selected items have reasons
    const missingReasons = selectedItems.filter(id => !returnReasons[id] || returnReasons[id].trim() === '');
    if (missingReasons.length > 0) {
      setError('Please provide a reason for all selected items');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const return_items = selectedItems.map(itemId => ({
        inventory_id: itemId,
        reason: returnReasons[itemId]
      }));

      // Get token from localStorage and include in headers
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_BASE_URL}/return_to_inventory.php`, {
        method: 'POST',
        credentials: 'include',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ return_items })
      });

      const result = await response.json();

      if (result.success) {
        alert(`✅ Successfully returned ${result.returned_count} item(s) to inventory`);
        onSuccess();
        handleClose();
      } else {
        setError(result.error || 'Failed to return items to inventory');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals for selected items
  const getSelectedTotals = () => {
    return selectedItems.reduce((totals, id) => {
      const item = shipment.items.find(i => i.id === id);
      if (item) {
        const looseBags = parseInt(item.loose_bags) || 0;
        totals.cartons += parseInt(item.cartons) || 0;
        totals.looseBags += looseBags;
        totals.totalPounds += calculateTotalPounds(item.pack_size, item.cartons, looseBags);
      }
      return totals;
    }, { cartons: 0, looseBags: 0, totalPounds: 0 });
  };

  if (!shipment) return null;

  const selectedTotals = getSelectedTotals();

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ background: 'linear-gradient(135deg, #d32f2f 0%, #1565c0 100%)', color: 'white' }}>
        <UndoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Return Shipment to Inventory - {shipment.fcl_no}
        <Typography variant="body2" color="white">
          {shipment.fcl_month} • {shipment.items.length} items
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Alert severity="info" sx={{ mt: 2, mb: 3 }}>
          <Typography variant="body2">
            <strong>This action will:</strong>
          </Typography>
          <ul style={{ marginTop: 8, marginBottom: 0 }}>
            <li>Change the status of selected items from <strong>SHIPPED</strong> to <strong>PRODUCED</strong></li>
            <li>Remove FCL details from the items</li>
            <li>Make the items available in Daily Inventory again</li>
            <li>Log the return action in the change logs</li>
          </ul>
        </Alert>

        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">
            Select items to return:
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={handleSelectAll}
          >
            {selectedItems.length === shipment.items.length ? 'Deselect All' : 'Select All'}
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ bgcolor: '#1565c0', color: 'white' }}>
                  <Checkbox
                    checked={selectedItems.length === shipment.items.length && shipment.items.length > 0}
                    indeterminate={selectedItems.length > 0 && selectedItems.length < shipment.items.length}
                    onChange={handleSelectAll}
                    sx={{ color: 'white', '&.Mui-checked': { color: 'white' }, '&.MuiCheckbox-indeterminate': { color: 'white' } }}
                  />
                </TableCell>
                <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold' }}>Pallet ID</TableCell>
                <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold' }}>Product</TableCell>
                <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold' }}>Grade</TableCell>
                <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold' }}>Pack Size</TableCell>
                <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold' }}>Brand</TableCell>
                <TableCell align="right" sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold' }}>Cartons</TableCell>
                <TableCell align="right" sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold' }}>Loose Bags</TableCell>
                <TableCell align="right" sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold' }}>Total Pounds</TableCell>
                <TableCell sx={{ bgcolor: '#1565c0', color: 'white', fontWeight: 'bold', minWidth: 180 }}>Return Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shipment.items.map((item) => {
                const isSelected = selectedItems.includes(item.id);
                const looseBags = parseInt(item.loose_bags) || 0;
                const totalPounds = calculateTotalPounds(item.pack_size, item.cartons, looseBags);
                return (
                  <TableRow key={item.id} hover selected={isSelected}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleItemSelect(item.id)}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{item.pallet_id || '-'}</TableCell>
                    <TableCell>{item.product}</TableCell>
                    <TableCell>{item.grade}</TableCell>
                    <TableCell>{item.pack_size || '-'}</TableCell>
                    <TableCell>{item.brand}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{item.cartons}</TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontWeight: 600, 
                        color: looseBags > 0 ? '#d32f2f' : 'text.secondary'
                      }}
                    >
                      {looseBags}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: '#1565c0' }}>
                      {totalPounds.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Reason (required)"
                        value={returnReasons[item.id] || ''}
                        onChange={(e) => handleReasonChange(item.id, e.target.value)}
                        disabled={!isSelected}
                        required={isSelected}
                        error={isSelected && (!returnReasons[item.id] || returnReasons[item.id].trim() === '')}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        {selectedItems.length > 0 && (
          <Paper sx={{ p: 2, mt: 2, bgcolor: 'info.light' }}>
            <Typography variant="body1" fontWeight="bold" gutterBottom>
              Summary:
            </Typography>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Chip label={`${selectedItems.length} items selected`} color="primary" />
              <Chip label={`${selectedTotals.cartons} cartons`} color="warning" />
              <Chip 
                label={`${selectedTotals.looseBags} loose bags`} 
                sx={{ 
                  bgcolor: selectedTotals.looseBags > 0 ? '#d32f2f' : 'default',
                  color: selectedTotals.looseBags > 0 ? 'white' : 'inherit'
                }}
              />
              <Chip 
                label={`${selectedTotals.totalPounds.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} LBs`} 
                color="success" 
              />
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSubmit}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
          disabled={loading || selectedItems.length === 0}
        >
          {loading ? 'Processing...' : 'Confirm Return to Inventory'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ReturnToInventoryModal;